import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Wrench, ArrowLeft } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/utils2";

export default function MagicLinkVerify() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { applyAuthPayload } = useAuth();
  const navigate = useNavigate();
  const ran = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    if (!token) {
      setError("This sign-in link is invalid. Please request a new one.");
      return;
    }

    (async () => {
      try {
        const { data } = await api.post("/auth/magic-link/verify", { token });
        applyAuthPayload({
          user_id: data.user_id,
          email: data.email,
          name: data.name,
          picture: data.picture,
          provider: data.provider,
          settings: data.settings,
          access_token: data.access_token,
        });
        navigate("/app", { replace: true });
      } catch (err) {
        setError(formatApiError(err));
      }
    })();
  }, [token, applyAuthPayload, navigate]);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex items-center justify-center p-6 noise-overlay">
      <div className="w-full max-w-md text-center">
        <div className="flex items-center justify-center gap-3 mb-10">
          <div className="h-11 w-11 grid place-items-center bg-orange-600 rounded-sm">
            <Wrench className="h-6 w-6 text-white" strokeWidth={2.5} />
          </div>
          <div className="font-display font-black text-2xl tracking-tighter">
            MAINTAIN<span className="text-orange-500">HQ</span>
          </div>
        </div>

        {!error ? (
          <>
            <div className="h-10 w-10 mx-auto border-2 border-orange-600 border-t-transparent rounded-full animate-spin mb-6" />
            <div className="text-[10px] uppercase tracking-[0.3em] text-orange-500 mb-2">// VERIFYING</div>
            <h2 className="font-display font-black text-2xl tracking-tight mb-2">Clocking you in...</h2>
            <p className="text-zinc-500 text-sm">Hang tight, opening the shop.</p>
          </>
        ) : (
          <>
            <div className="text-[10px] uppercase tracking-[0.3em] text-red-500 mb-2">// LINK ERROR</div>
            <h2 className="font-display font-black text-2xl tracking-tight mb-3">Something's off.</h2>
            <div className="border border-red-900/50 bg-red-950/40 text-red-300 px-3 py-3 text-sm rounded-sm mb-6" data-testid="magic-error">
              {error}
            </div>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-xs text-orange-500 hover:text-orange-400 uppercase tracking-widest font-bold"
            >
              <ArrowLeft className="w-3 h-3" /> Request a new link
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
